import moment from "moment";
import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { FieldErrorMessage } from "../ui/Text";
import { Validation } from "@/types/form";
import useClickOutside from "@/hooks/useClickOutSide";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { FORMATE } from "@/utils/constant/global.constant";

interface DateInputValidation extends Validation {
  minDate?: string;
  maxDate?: string;
  maxToday?: boolean;
}
interface DateInputProps {
  label: string;
  field: string;
  placeholder: string;
  disabled?: boolean;
  validation: DateInputValidation;
  defaultValue?: string;
}

const DateInput: React.FC<DateInputProps> = ({ field, disabled = false, validation, placeholder, defaultValue }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    if (defaultValue) {
      return moment(defaultValue, [FORMATE.DATE, "YYYY-MM-DD"]);
    }
    return moment();
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const dropdownRef = useClickOutside(() => setShowCalendar(false));
  const [view] = useState<"month" | "year">("month");

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const errorMessage: any = errors[field]?.message;

  const handleNavigation = (direction: "prev" | "next") => {
    if (view === "month") {
      setCurrentDate(direction === "next" ? moment(currentDate).add(1, "month") : moment(currentDate).subtract(1, "month"));
    } else {
      setCurrentDate(direction === "next" ? moment(currentDate).add(1, "year") : moment(currentDate).subtract(1, "year"));
    }
  };
  const isValidDate = (date: moment.Moment) => {
    const minDateMoment = moment(validation?.minDate);
    const today = moment();
    return date.isSameOrAfter(minDateMoment, "day") && (!validation?.maxToday || date.isSameOrBefore(today, "day"));
  };

  const generateCalendarDays = () => {
    const firstDayOfMonth = moment(currentDate).startOf("month");
    const lastDayOfPrevMonth = moment(currentDate).subtract(1, "month").endOf("month");
    const startDay = firstDayOfMonth.day();

    const days = [];

    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = moment(lastDayOfPrevMonth).subtract(i, "days");

      days.push({
        date: lastDayOfPrevMonth.date() - i,
        isCurrentMonth: false,
        month: "prev",
        isValid: isValidDate(prevDate),
      });
    }

    // Current month days
    const daysInMonth = currentDate.daysInMonth();
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = moment(currentDate).date(i);

      days.push({
        date: i,
        isCurrentMonth: true,
        month: "current",
        isValid: isValidDate(currentDay),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = moment(currentDate).add(1, "month").date(i);

      days.push({
        date: i,
        isCurrentMonth: false,
        month: "next",
        isValid: isValidDate(nextDate),
      });
    }

    return days;
  };

  const dateValidationRules = {
    required: validation?.required ? "This field is required" : false,
    validate: (value: Date) => {
      if (validation.minDate && value < new Date(validation.minDate)) {
        return `Date must be after ${moment(validation.minDate).format(FORMATE.DATE)}`;
      }
      if (validation.maxDate && value > new Date(validation.maxDate)) {
        return `Date must be before ${moment(validation.maxDate).format(FORMATE.DATE)}`;
      }
      if (validation.maxToday && value > new Date()) {
        return "Date cannot be in the future";
      }
      return true;
    },
  };

  useEffect(() => {
    if (defaultValue) {
      setValue(field, defaultValue, { shouldDirty: false });
      setCurrentDate(moment(defaultValue, [FORMATE.DATE, "YYYY-MM-DD"]));
    }
  }, [defaultValue, setValue, field]);

  return (
    <div className="relative w-full">
      <Controller
        name={field}
        control={control}
        rules={dateValidationRules}
        render={({ field: { onChange, value } }) => (
          <div className="relative w-full text-13">
            <div className="relative w-full">
              <input
                type="text"
                value={value ? moment(value).format(FORMATE.DATE) : ""}
                onClick={() => {
                  if (defaultValue) {
                    setCurrentDate(moment(defaultValue, [FORMATE.DATE, "YYYY-MM-DD"]));
                  }
                  setShowCalendar(true);
                }}
                placeholder={placeholder}
                readOnly
                className={`
                w-full px-3 py-2 text-text-default rounded-lg outline-none border
                ${errors[field]?.message ? "border-text-error" : "border-fieldBorder"}
                ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                hover:border-gray-400 focus:border-text-primary-500
              `}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Calendar className="text-gray-400 size-4" />
              </span>
            </div>

            {showCalendar && (
              <div className="absolute left-0 w-full min-w-64 border rounded-lg shadow-calender z-[100] bg-white top-full mt-2" ref={dropdownRef}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4 border-b-1 p-2 py-4">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigation("prev");
                    }}
                    className="hover:bg-gray-100"
                  >
                    <ChevronLeft className=" text-icon-default2 size-4" />
                  </button>
                  <div className=" flex justify-center flex-1 gap-2 flex-wrap">
                    <div className=" flex items-center gap-2">
                      <select
                        value={currentDate.year()}
                        onChange={(e) => {
                          setCurrentDate(moment(currentDate).year(parseInt(e.target.value)));
                        }}
                        className="w-fit px-2 py-0.5  text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                      >
                        {Array.from({ length: moment().year() - moment(validation?.minDate).year() + 1 }, (_, i) => moment(validation?.minDate).year() + i).map(
                          (year) => {
                            // Check if year is within valid range
                            const isBeforeMax = validation?.maxDate ? year <= moment(validation?.maxDate).year() : true;
                            const isBeforeToday = validation?.maxToday ? year <= moment().year() : true;

                            // Only return option if year is valid
                            if (!isBeforeMax || !isBeforeToday) return null;

                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          }
                        )}
                      </select>
                      <select
                        value={currentDate.month()}
                        onChange={(e) => {
                          setCurrentDate(moment(currentDate).month(parseInt(e.target.value)));
                        }}
                        className="w-fit px-2 py-0.5  text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                      >
                        {moment.months().map((month, index) => (
                          <option key={month} value={index}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* 
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setView("month")}
                        className={`px-2 py-0.5  rounded-sm bg-white border text-13 leading-normal   ${
                          view === "month" ? " text-text-primary border-text-primary " : " text-text-default border-white"
                        }`}
                      >
                        Month
                      </button>
                      <button
                        onClick={() => setView("year")}
                        className={`px-2 py-0.5  rounded-sm bg-white border text-13 leading-normal  ${
                          view === "year" ? "text-text-primary border-text-primary" : "text-text-default border-white"
                        }`}
                      >
                        Year
                      </button>
                    </div> */}
                  </div>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigation("next");
                    }}
                    className="hover:bg-gray-100"
                  >
                    <ChevronRight className="text-icon-default2 size-4" />
                  </button>
                </div>
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 p-2">
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <div key={day} className="text-center  font-medium py-1 text-gray-600">
                      {day}
                    </div>
                  ))}

                  {generateCalendarDays().map((day, index) => (
                    <button
                      key={index}
                      disabled={!day.isValid}
                      onClick={() => {
                        const newDate = moment(currentDate);
                        if (day.month === "prev") {
                          newDate.subtract(1, "month");
                        } else if (day.month === "next") {
                          newDate.add(1, "month");
                        }
                        newDate.date(day.date);
                        onChange(newDate.format("YYYY-MM-DD"));
                        setShowCalendar(false);
                      }}
                      className={`
                        p-2 text-center
                        ${!day.isCurrentMonth ? "text-gray-400" : "text-gray-700"}
                        ${!day.isValid ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}
                        ${
                          moment(value).isSame(moment(currentDate).clone().set("date", day.date), "day") && day.isCurrentMonth
                            ? "bg-purple-100 text-purple-600"
                            : "hover:bg-gray-100"
                        }
                      `}
                    >
                      {day.date}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      />
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default DateInput;
